/**
 * Check Native Auth Status
 *
 * Utility to verify native mobile app has valid auth tokens
 * before proceeding with operations that require authentication.
 * Uses message passing between web and native.
 */

import { isInMobileWebView } from './sendNativeAuth';

/**
 * Response from native app for auth status check
 */
export interface NativeAuthStatusResponse {
  type: 'NATIVE_AUTH_STATUS';
  isAuthenticated: boolean;
  userId: string | null;
}

/**
 * Message listener cleanup function
 */
type CleanupFunction = () => void;

/**
 * Default timeout for native auth check (5 seconds)
 */
const DEFAULT_TIMEOUT_MS = 5000;

/**
 * Check if native app has valid authentication tokens
 *
 * Sends CHECK_NATIVE_AUTH message to native and waits for NATIVE_AUTH_STATUS response.
 * Returns authentication status or false on timeout/error.
 *
 * @param timeoutMs - Maximum time to wait for response (default: 5000ms)
 * @returns Promise resolving to auth status object or null on timeout/error
 */
export async function checkNativeAuthStatus(
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<NativeAuthStatusResponse | null> {
  // Not in WebView - return null (caller should handle this case)
  if (!isInMobileWebView()) {
    return null;
  }

  return new Promise((resolve) => {
    let cleanup: CleanupFunction | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Message handler for native response
    const handleMessage = (event: MessageEvent) => {
      try {
        // Parse message data (could be string or object)
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.type === 'NATIVE_AUTH_STATUS') {
          // Clean up and resolve
          if (cleanup) cleanup();
          resolve({
            type: 'NATIVE_AUTH_STATUS',
            isAuthenticated: Boolean(data.isAuthenticated),
            userId: data.userId ?? null,
          });
        }
      } catch {
        // Ignore non-JSON messages or parse errors
      }
    };

    // Set up message listener
    window.addEventListener('message', handleMessage);

    // Cleanup function
    cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // Set timeout
    timeoutId = setTimeout(() => {
      console.warn(
        '[checkNativeAuth] Timeout waiting for NATIVE_AUTH_STATUS response',
      );
      if (cleanup) cleanup();
      resolve(null);
    }, timeoutMs);

    // Send check message to native
    (
      window as unknown as {
        ReactNativeWebView: { postMessage: (msg: string) => void };
      }
    ).ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'CHECK_NATIVE_AUTH' }),
    );
  });
}

/**
 * Verify native auth is established before proceeding
 *
 * Convenience wrapper that checks auth status and validates against expected userId.
 *
 * @param expectedUserId - The userId that should be authenticated (optional)
 * @param timeoutMs - Maximum time to wait for response
 * @returns Promise resolving to true if authenticated (and userId matches if provided)
 */
export async function verifyNativeAuth(
  expectedUserId?: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<boolean> {
  // Not in WebView - allow operation (web handles auth differently)
  if (!isInMobileWebView()) {
    return true;
  }

  const status = await checkNativeAuthStatus(timeoutMs);

  // Timeout or error - assume old app version that doesn't support CHECK_NATIVE_AUTH
  // For backwards compatibility, allow the operation to proceed
  if (!status) {
    console.warn(
      '[checkNativeAuth] No response from native (likely old app version), allowing operation for backwards compatibility',
    );
    return true;
  }

  // Not authenticated
  if (!status.isAuthenticated) {
    console.error('[checkNativeAuth] Native app is not authenticated');
    return false;
  }

  // If expectedUserId provided, verify it matches
  if (expectedUserId && status.userId !== expectedUserId) {
    console.error('[checkNativeAuth] User ID mismatch', {
      expected: expectedUserId,
      actual: status.userId,
    });
    return false;
  }

  return true;
}
